import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Lightbulb, Loader2, Sparkles, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { getApiBase } from '../../utils/apiBase';
import { toast } from 'sonner';

const API = getApiBase();

const CONTEXT_ACTIONS = {
  composer: [{ action: 'suggest_prompts', label: 'Suggest prompts' }],
  transcript: [
    { action: 'improve_title', label: 'Improve title' },
    { action: 'chapter_intro', label: 'Draft chapter intro' },
    { action: 'suggest_date', label: 'Suggest timeline date' },
    { action: 'find_similar', label: 'Check for similar memories' },
  ],
  book: [
    { action: 'suggest_prompts', label: 'Suggest what to write next' },
    { action: 'suggest_chapter_order', label: 'Suggest chapter order' },
  ],
  export: [{ action: 'export_prep', label: 'Export readiness tips' }],
};

function dismissKey(surface, id) {
  return `story-guide-dismiss-${surface}-${id || 'global'}`;
}

/**
 * Inline, optional writing companion — not a floating chatbot.
 */
export function StoryGuidePanel({
  surface,
  storyId,
  treeId,
  className,
  onApplyPrompt,
  onApplyTitle,
  onApplyIntro,
  onApplyDate,
  onApplyChapterOrder,
  /** When false, panel stays hidden (e.g. story not saved yet). */
  enabled = true,
}) {
  const storageKey = dismissKey(surface, storyId || treeId);
  const [collapsed, setCollapsed] = useState(false);
  const [serviceStatus, setServiceStatus] = useState(enabled ? 'checking' : 'off');
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  });
  const [loadingAction, setLoadingAction] = useState(null);
  const [result, setResult] = useState(null);
  const [creditLabel, setCreditLabel] = useState(null);

  const actions = CONTEXT_ACTIONS[surface] || [];

  useEffect(() => {
    if (!enabled || dismissed) {
      setServiceStatus('off');
      return;
    }
    if (surface === 'transcript' && !storyId) {
      setServiceStatus('off');
      return;
    }
    if ((surface === 'book' || surface === 'export' || surface === 'composer') && !treeId) {
      setServiceStatus('off');
      return;
    }
    let cancelled = false;
    const verify = async () => {
      try {
        if (surface === 'transcript' && storyId) {
          await axios.get(`${API}/stories/${storyId}`);
        } else if (treeId) {
          await axios.get(`${API}/trees/${treeId}`);
        }
        if (!cancelled) setServiceStatus('ready');
      } catch (e) {
        if (!cancelled && e.response?.status === 404) {
          setServiceStatus('unavailable');
        } else if (!cancelled) {
          setServiceStatus('ready');
        }
      }
    };
    setServiceStatus('checking');
    verify();
    return () => {
      cancelled = true;
    };
  }, [enabled, dismissed, storyId, treeId, surface]);

  const runAction = useCallback(
    async (action) => {
      setLoadingAction(action);
      setResult(null);
      setCreditLabel(null);
      try {
        const isTree = ['suggest_chapter_order', 'export_prep'].includes(action) ||
          (action === 'suggest_prompts' && surface === 'book');
        const url = isTree
          ? `${API}/trees/${treeId}/guide`
          : `${API}/stories/${storyId}/guide`;
        const body = isTree ? { action, story_id: storyId || undefined } : { action };
        const res = await axios.post(url, body);
        setResult(res.data?.result || {});
        setCreditLabel(res.data?.credit_label || null);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Companion unavailable right now');
      } finally {
        setLoadingAction(null);
      }
    },
    [storyId, treeId, surface]
  );

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(storageKey, '1');
    } catch {
      /* ignore */
    }
  };

  if (dismissed || !actions.length || (!storyId && !treeId) || !enabled || serviceStatus === 'off') {
    return null;
  }

  if (serviceStatus === 'checking') {
    return null;
  }

  if (serviceStatus === 'unavailable') {
    return (
      <aside
        className={cn(
          'rounded-xl border border-dashed border-[hsl(38_14%_86%)] bg-muted/10 px-4 py-3 text-xs text-muted-foreground',
          className
        )}
        data-testid="story-guide-unavailable"
      >
        Writing companion — coming back soon. Your draft saves normally without it.
      </aside>
    );
  }

  const renderResults = () => {
    if (!result) return null;
    if (result.prompts?.length) {
      return (
        <ul className="mt-3 space-y-2" role="list">
          {result.prompts.map((p) => (
            <li key={p} className="flex items-start justify-between gap-2 rounded-lg border border-[hsl(38_16%_88%)] bg-white/70 px-3 py-2 text-sm">
              <span className="text-[hsl(32_10%_38%)]">{p}</span>
              {onApplyPrompt ? (
                <Button type="button" size="sm" variant="ghost" className="shrink-0 text-xs" onClick={() => onApplyPrompt(p)}>
                  Use
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      );
    }
    if (result.titles?.length) {
      return (
        <ul className="mt-3 space-y-2" role="list">
          {result.titles.map((t) => (
            <li key={t} className="flex items-center justify-between gap-2 rounded-lg border border-[hsl(38_16%_88%)] bg-white/70 px-3 py-2 text-sm">
              <span className="font-serif text-[hsl(24_14%_16%)]">{t}</span>
              {onApplyTitle ? (
                <Button type="button" size="sm" variant="ghost" className="text-xs" onClick={() => onApplyTitle(t)}>
                  Use
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      );
    }
    if (result.intro) {
      return (
        <div className="mt-3 rounded-lg border border-[hsl(38_16%_88%)] bg-white/70 p-3 text-sm leading-relaxed text-[hsl(32_10%_38%)]">
          <p>{result.intro}</p>
          {onApplyIntro ? (
            <Button type="button" size="sm" variant="outline" className="mt-2 rounded-full text-xs" onClick={() => onApplyIntro(result.intro)}>
              Copy into your draft
            </Button>
          ) : null}
        </div>
      );
    }
    if (result.suggested_date !== undefined) {
      return (
        <div className="mt-3 rounded-lg border border-[hsl(38_16%_88%)] bg-white/70 p-3 text-sm">
          {result.suggested_date ? (
            <>
              <p className="font-serif text-[hsl(24_14%_16%)]">{result.suggested_date}</p>
              {result.note ? <p className="mt-1 text-xs text-muted-foreground">{result.note}</p> : null}
              {onApplyDate && result.suggested_date ? (
                <Button type="button" size="sm" variant="outline" className="mt-2 rounded-full text-xs" onClick={() => onApplyDate(result.suggested_date)}>
                  Use date
                </Button>
              ) : null}
            </>
          ) : (
            <p className="text-muted-foreground">No clear date found — that is okay. Add one only if it feels right.</p>
          )}
        </div>
      );
    }
    if (result.similar?.length) {
      return (
        <ul className="mt-3 space-y-2" role="list">
          {result.similar.map((s) => (
            <li key={s.story_id} className="rounded-lg border border-amber-200/80 bg-amber-50/50 px-3 py-2 text-xs text-[hsl(32_10%_38%)]">
              <span className="font-medium">{s.title}</span> — {s.reason} ({Math.round((s.similarity || 0) * 100)}% alike)
            </li>
          ))}
        </ul>
      );
    }
    if (result.similar && !result.similar.length) {
      return <p className="mt-3 text-sm text-muted-foreground">No very similar memories found — you are clear to keep writing.</p>;
    }
    if (result.ordered_titles?.length) {
      return (
        <div className="mt-3 space-y-2">
          <ol className="list-decimal space-y-1 pl-5 text-sm text-[hsl(32_10%_38%)]">
            {result.ordered_titles.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ol>
          {result.note ? <p className="text-xs text-muted-foreground">{result.note}</p> : null}
          {onApplyChapterOrder && result.story_order ? (
            <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" onClick={() => onApplyChapterOrder(result.story_order)}>
              Open audiobook order
            </Button>
          ) : null}
        </div>
      );
    }
    if (result.tips?.length) {
      return (
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-[hsl(32_10%_38%)]">
          {result.tips.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      );
    }
    return null;
  };

  return (
    <aside
      className={cn(
        'rounded-xl border border-[hsl(43_48%_58%_/_0.25)] bg-[hsl(42_28%_99%)]/90',
        className
      )}
      data-testid={`story-guide-${surface}`}
      aria-label="Writing companion"
    >
      <div className="flex items-start justify-between gap-2 px-3 py-2.5 sm:px-4">
        <div className="flex min-w-0 items-start gap-2">
          <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-[#8b7355]" aria-hidden />
          <div className="min-w-0">
            <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(32_10%_42%)]">
              Writing companion
            </p>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Optional ideas when you want them — you stay the author.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
          >
            {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </button>
          <button type="button" className="rounded-md p-1 text-muted-foreground hover:text-foreground" onClick={dismiss} aria-label="Dismiss writing companion">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      {!collapsed ? (
        <div className="border-t border-[hsl(38_14%_88%)]/60 px-3 pb-3 pt-2 sm:px-4">
          <div className="flex flex-wrap gap-2">
            {actions.map(({ action, label }) => (
              <Button
                key={action}
                type="button"
                size="sm"
                variant="outline"
                className="rounded-full text-xs"
                disabled={loadingAction != null}
                onClick={() => runAction(action)}
                data-testid={`story-guide-action-${action}`}
              >
                {loadingAction === action ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className="mr-1 h-3 w-3 text-[#8b7355]" />
                )}
                {label}
              </Button>
            ))}
          </div>
          {renderResults()}
          {creditLabel ? <p className="mt-2 text-[10px] text-muted-foreground">{creditLabel}</p> : null}
        </div>
      ) : null}
    </aside>
  );
}

export default StoryGuidePanel;
